import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Button,
  Tab,
  TabList,
  Toast,
  ToastTitle,
  Toaster,
  mergeClasses,
  useId,
  useToastController,
} from '@fluentui/react-components';
import { ArrowClockwise20Regular } from '@fluentui/react-icons';
import { useTranslation } from 'react-i18next';
import { Consultorio, QueueDisplayScreen, Sector, Sede } from '../../../../models/Entities/Turnero';
import { ErrorType } from '../../../../models/HttpError';
import { connector, Props } from './OfficesWorkspace.Functions';
import { useOfficesWorkspaceStyles } from './OfficesWorkspace.Styles';
import {
  CONSULTORIO_STATUS,
  computeOfficesSummary,
  findById,
  getSectorSedeId,
  normalizeConsultorioStatus,
  useRequestOutcome,
} from '../Offices.Functions';
import { ErrorBanner } from '../OfficesCommon/OfficesCommon.Component';
import SedesTab from '../SedesTab/SedesTab.Component';
import ConsultoriosTab, { ConsultoriosFilter } from '../ConsultoriosTab/ConsultoriosTab.Component';
import DisplaysTab, { DisplaysFilter } from '../DisplaysTab/DisplaysTab.Component';
import SedeDrawer from '../SedeDrawer/SedeDrawer.Component';
import SectorDrawer from '../SectorDrawer/SectorDrawer.Component';
import ConsultorioDrawer from '../ConsultorioDrawer/ConsultorioDrawer.Component';
import DisplayDrawer from '../DisplayDrawer/DisplayDrawer.Component';
import DisplayPairingDialog from '../DisplayPairingDialog/DisplayPairingDialog.Component';
import OfficesConfirmDialog from '../OfficesConfirmDialog/OfficesConfirmDialog.Component';

export type OfficesTab = 'structure' | 'consultorios' | 'displays';

type ConfirmAction =
  | { kind: 'deleteSede'; sede: Sede }
  | { kind: 'deleteSector'; sector: Sector }
  | { kind: 'deleteConsultorio'; consultorio: Consultorio }
  | { kind: 'toggleConsultorio'; consultorio: Consultorio; next: string }
  | { kind: 'deleteDisplay'; display: QueueDisplayScreen }
  | { kind: 'testCall'; display: QueueDisplayScreen; medicoId: string };

type SaveOp = 'create' | 'update';

const fallbackError = (message: string): ErrorType => ({ ErrorCode: 0, ErrorMessage: message, Errors: [] });

export const OfficesWorkspace = (props: Props) => {
  const {
    sedes,
    sectores,
    consultorios,
    displays,
    sedesStatus,
    sectoresStatus,
    consultoriosStatus,
    displaysStatus,
    llamadoStatus,
  } = props;
  const { t } = useTranslation();
  const styles = useOfficesWorkspaceStyles();
  const toasterId = useId('offices-toaster');
  const { dispatchToast } = useToastController(toasterId);

  const notify = useCallback(
    (message: string, intent: 'success' | 'warning' | 'error' = 'success') =>
      dispatchToast(
        <Toast>
          <ToastTitle>{message}</ToastTitle>
        </Toast>,
        { intent, timeout: 5000 }
      ),
    [dispatchToast]
  );

  // --- Carga ------------------------------------------------------------------------------------
  const loadAll = () => {
    props.GetSedes();
    props.GetSectores();
    props.GetConsultorios();
    props.GetQueueDisplayScreens();
  };
  useEffect(() => {
    loadAll();
    // Carga única: los filtros son locales (B17).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const retryFailedLoads = () => {
    if (sedesStatus.loadFailed) props.GetSedes();
    if (sectoresStatus.loadFailed) props.GetSectores();
    if (consultoriosStatus.loadFailed) props.GetConsultorios();
    if (displaysStatus.loadFailed) props.GetQueueDisplayScreens();
  };
  const loadError = [sedesStatus, sectoresStatus, consultoriosStatus, displaysStatus].find((s) => s.loadFailed);

  // --- Navegación y filtros ----------------------------------------------------------------------
  const [tab, setTab] = useState<OfficesTab>('structure');
  const [consultoriosFilter, setConsultoriosFilter] = useState<ConsultoriosFilter>({ search: '', sedeId: null, sectorId: null, status: 'all' });
  const [displaysFilter, setDisplaysFilter] = useState<DisplaysFilter>({ search: '', sedeId: null, sectorId: null, status: 'all' });
  const [selectedSectorId, setSelectedSectorId] = useState<number | null>(null);
  const [selectedConsultorioId, setSelectedConsultorioId] = useState<number | undefined>();
  const [selectedDisplayId, setSelectedDisplayId] = useState<number | undefined>();

  const showSectorIn = (target: 'consultorios' | 'displays', sector: Sector) => {
    const location = { sectorId: sector.id ?? null, sedeId: getSectorSedeId(sector) ?? null, search: '' };
    if (target === 'consultorios') setConsultoriosFilter({ ...location, status: 'all' });
    else setDisplaysFilter({ ...location, status: 'all' });
    setSelectedSectorId(sector.id ?? null);
    setTab(target);
  };

  const summary = useMemo(() => computeOfficesSummary(sedes, sectores, consultorios, displays), [sedes, sectores, consultorios, displays]);

  // --- Sede: crear / editar ----------------------------------------------------------------------
  const [sedeDrawer, setSedeDrawer] = useState<{ open: boolean; sede?: Sede }>({ open: false });
  const [sedeOp, setSedeOp] = useState<SaveOp>('create');
  const [sedeSaveError, setSedeSaveError] = useState<ErrorType | undefined>();
  const armSede = useRequestOutcome(
    sedeOp === 'create' ? sedesStatus.isAdding : sedesStatus.isUpdating,
    sedeOp === 'create' ? sedesStatus.addFailed : sedesStatus.updateFailed,
    (ok) => {
      if (!ok) return setSedeSaveError(sedesStatus.error || fallbackError(t('Offices.messages.unexpected')));
      setSedeDrawer({ open: false });
      notify(sedeOp === 'create' ? t('Offices.messages.sedeCreated') : t('Offices.messages.sedeUpdated'));
      props.GetSedes();
    }
  );
  const openSede = (sede?: Sede) => {
    setSedeSaveError(undefined);
    setSedeDrawer({ open: true, sede });
  };
  const submitSede = (payload: Sede) => {
    const op: SaveOp = payload.id !== undefined ? 'update' : 'create';
    setSedeOp(op);
    setSedeSaveError(undefined);
    armSede();
    if (op === 'update') props.UpdateSede(payload.id as number, payload);
    else props.CreateSede(payload);
  };

  // --- Sector: crear / editar (incluye asignación de consultorios) ------------------------------
  const [sectorDrawer, setSectorDrawer] = useState<{ open: boolean; sector?: Sector; defaultSedeId?: number }>({ open: false });
  const [sectorOp, setSectorOp] = useState<SaveOp>('create');
  const [sectorSaveError, setSectorSaveError] = useState<ErrorType | undefined>();
  const armSector = useRequestOutcome(
    sectorOp === 'create' ? sectoresStatus.isAdding : sectoresStatus.isUpdating,
    sectorOp === 'create' ? sectoresStatus.addFailed : sectoresStatus.updateFailed,
    (ok) => {
      if (!ok) return setSectorSaveError(sectoresStatus.error || fallbackError(t('Offices.messages.unexpected')));
      setSectorDrawer({ open: false });
      notify(sectorOp === 'create' ? t('Offices.messages.sectorCreated') : t('Offices.messages.sectorUpdated'));
      // La asignación de consultorios cambia el sector de cada consultorio.
      props.GetSectores();
      props.GetConsultorios();
    }
  );
  const openSector = (sector?: Sector, defaultSedeId?: number) => {
    setSectorSaveError(undefined);
    if (sector?.id !== undefined) setSelectedSectorId(sector.id);
    setSectorDrawer({ open: true, sector, defaultSedeId });
  };
  const startSectorSave = (op: SaveOp) => {
    setSectorOp(op);
    setSectorSaveError(undefined);
    armSector();
  };

  // --- Consultorio: crear / editar ---------------------------------------------------------------
  const [consultorioDrawer, setConsultorioDrawer] = useState<{ open: boolean; consultorio?: Consultorio }>({ open: false });
  const [consultorioOp, setConsultorioOp] = useState<SaveOp>('create');
  const [consultorioSaveError, setConsultorioSaveError] = useState<ErrorType | undefined>();
  const armConsultorio = useRequestOutcome(
    consultorioOp === 'create' ? consultoriosStatus.isAdding : consultoriosStatus.isUpdating,
    consultorioOp === 'create' ? consultoriosStatus.addFailed : consultoriosStatus.updateFailed,
    (ok) => {
      if (!ok) return setConsultorioSaveError(consultoriosStatus.error || fallbackError(t('Offices.messages.unexpected')));
      setConsultorioDrawer({ open: false });
      notify(consultorioOp === 'create' ? t('Offices.messages.consultorioCreated') : t('Offices.messages.consultorioUpdated'));
      props.GetConsultorios();
      props.GetSectores();
    }
  );
  const openConsultorio = (consultorio?: Consultorio) => {
    setConsultorioSaveError(undefined);
    setSelectedConsultorioId(consultorio?.id);
    setConsultorioDrawer({ open: true, consultorio });
  };
  const submitConsultorio = (payload: Consultorio, id?: number) => {
    const op: SaveOp = id !== undefined ? 'update' : 'create';
    setConsultorioOp(op);
    setConsultorioSaveError(undefined);
    armConsultorio();
    if (op === 'update') props.UpdateConsultorio(id as number, payload);
    else props.CreateConsultorio(payload);
  };

  // --- Display: crear / editar -------------------------------------------------------------------
  const [displayDrawer, setDisplayDrawer] = useState<{ open: boolean; display?: QueueDisplayScreen }>({ open: false });
  const [displayOp, setDisplayOp] = useState<SaveOp>('create');
  const [displaySaveError, setDisplaySaveError] = useState<ErrorType | undefined>();
  const armDisplay = useRequestOutcome(
    displayOp === 'create' ? displaysStatus.isAdding : displaysStatus.isUpdating,
    displayOp === 'create' ? displaysStatus.addFailed : displaysStatus.updateFailed,
    (ok) => {
      if (!ok) return setDisplaySaveError(displaysStatus.error || fallbackError(t('Offices.messages.unexpected')));
      setDisplayDrawer({ open: false });
      notify(displayOp === 'create' ? t('Offices.messages.displayCreated') : t('Offices.messages.displayUpdated'));
      props.GetQueueDisplayScreens();
    }
  );
  const openDisplay = (display?: QueueDisplayScreen) => {
    setDisplaySaveError(undefined);
    setSelectedDisplayId(display?.id);
    setDisplayDrawer({ open: true, display });
  };
  const submitDisplay = (payload: QueueDisplayScreen, id?: number) => {
    const op: SaveOp = id !== undefined ? 'update' : 'create';
    setDisplayOp(op);
    setDisplaySaveError(undefined);
    armDisplay();
    if (op === 'update') props.UpdateQueueDisplayScreen(id as number, payload);
    else props.CreateQueueDisplayScreen(payload);
  };
  // El display que se edita se toma de la lista vigente por id (nunca una copia vieja).
  const editingDisplay = displayDrawer.display?.id !== undefined ? findById(displays, displayDrawer.display.id) ?? displayDrawer.display : undefined;

  // --- Vincular display -------------------------------------------------------------------------
  const [pairingOpen, setPairingOpen] = useState(false);

  // --- Acciones con confirmación (eliminar, habilitar/deshabilitar, llamado de prueba) -----------
  const [confirm, setConfirm] = useState<ConfirmAction | null>(null);
  const [running, setRunning] = useState<ConfirmAction | null>(null);
  const [actionError, setActionError] = useState<{ action: ConfirmAction; error?: ErrorType } | null>(null);
  const statusCheck = useRef<{ id: number; expected: string } | null>(null);

  const actionFlags = (action: ConfirmAction | null): { busy: boolean; failed: boolean; error?: ErrorType } => {
    switch (action?.kind) {
      case 'deleteSede':
        return { busy: sedesStatus.isDeleting, failed: sedesStatus.deleteFailed, error: sedesStatus.error };
      case 'deleteSector':
        return { busy: sectoresStatus.isDeleting, failed: sectoresStatus.deleteFailed, error: sectoresStatus.error };
      case 'deleteConsultorio':
        return { busy: consultoriosStatus.isDeleting, failed: consultoriosStatus.deleteFailed, error: consultoriosStatus.error };
      case 'toggleConsultorio':
        return { busy: consultoriosStatus.isUpdatingEstado, failed: consultoriosStatus.updateEstadoFailed, error: consultoriosStatus.error };
      case 'deleteDisplay':
        return { busy: displaysStatus.isDeleting, failed: displaysStatus.deleteFailed, error: displaysStatus.error };
      case 'testCall':
        return { busy: llamadoStatus.isCreating, failed: llamadoStatus.createFailed, error: llamadoStatus.error };
      default:
        return { busy: false, failed: false };
    }
  };
  const runningFlags = actionFlags(running);

  const armAction = useRequestOutcome(runningFlags.busy, runningFlags.failed, (ok) => {
    const action = running;
    setConfirm(null);
    setRunning(null);
    if (!action) return;
    if (!ok) {
      setActionError({ action, error: runningFlags.error });
      return;
    }
    setActionError(null);
    switch (action.kind) {
      case 'deleteSede':
        notify(t('Offices.messages.sedeDeleted'));
        loadAll();
        break;
      case 'deleteSector':
        notify(t('Offices.messages.sectorDeleted'));
        if (selectedSectorId === action.sector.id) setSelectedSectorId(null);
        props.GetSectores();
        props.GetConsultorios();
        props.GetQueueDisplayScreens();
        break;
      case 'deleteConsultorio':
        notify(t('Offices.messages.consultorioDeleted'));
        props.GetSectores();
        break;
      case 'toggleConsultorio':
        statusCheck.current = { id: action.consultorio.id as number, expected: action.next };
        armStatusCheck();
        props.GetConsultorios();
        break;
      case 'deleteDisplay':
        notify(t('Offices.messages.displayDeleted'));
        break;
      case 'testCall':
        notify(t('Offices.messages.testCallSent', { name: action.display.name || action.display.registrationNumber }));
        break;
    }
  });

  // B-BE-3: el back legacy responde OK pero no guarda el estado. Se verifica releyendo la lista.
  const armStatusCheck = useRequestOutcome(consultoriosStatus.isLoading, consultoriosStatus.loadFailed, (ok) => {
    const check = statusCheck.current;
    statusCheck.current = null;
    if (!ok || !check) return;
    const fresh = findById(consultorios, check.id);
    if (fresh && normalizeConsultorioStatus(fresh.status) !== normalizeConsultorioStatus(check.expected)) {
      notify(t('Offices.messages.statusNotPersisted'), 'warning');
    } else {
      notify(t('Offices.messages.consultorioStatusUpdated'));
    }
  });

  const runAction = (action: ConfirmAction) => {
    setRunning(action);
    setActionError(null);
    armAction();
    switch (action.kind) {
      case 'deleteSede':
        return props.DeleteSede(action.sede.id as number);
      case 'deleteSector':
        return props.DeleteSector(action.sector.id as number);
      case 'deleteConsultorio':
        return props.DeleteConsultorio(action.consultorio.id as number);
      case 'toggleConsultorio':
        return props.UpdateEstadoConsultorio(action.consultorio.id as number, action.next);
      case 'deleteDisplay':
        return props.DeleteQueueDisplayScreen(action.display.id as number);
      case 'testCall':
        return props.CrearLlamado({ medicoId: action.medicoId, nombrePaciente: t('Offices.display.testCallPatient'), isTest: true });
    }
  };

  const confirmTexts = (action: ConfirmAction): { title: string; message: string; confirmLabel: string; danger: boolean } => {
    switch (action.kind) {
      case 'deleteSede':
        return {
          title: t('Offices.confirm.deleteSedeTitle'),
          message: t('Offices.confirm.deleteSedeMessage', { name: action.sede.name }),
          confirmLabel: t('Offices.actions.delete'),
          danger: true,
        };
      case 'deleteSector':
        return {
          title: t('Offices.confirm.deleteSectorTitle'),
          message: t('Offices.confirm.deleteSectorMessage', { name: action.sector.name }),
          confirmLabel: t('Offices.actions.delete'),
          danger: true,
        };
      case 'deleteConsultorio':
        return {
          title: t('Offices.confirm.deleteConsultorioTitle'),
          message: t('Offices.confirm.deleteConsultorioMessage', { name: action.consultorio.name }),
          confirmLabel: t('Offices.actions.delete'),
          danger: true,
        };
      case 'toggleConsultorio': {
        const enabling = action.next === CONSULTORIO_STATUS.disponible;
        return {
          title: enabling ? t('Offices.confirm.enableConsultorioTitle') : t('Offices.confirm.disableConsultorioTitle'),
          message: t(enabling ? 'Offices.confirm.enableConsultorioMessage' : 'Offices.confirm.disableConsultorioMessage', {
            name: action.consultorio.name,
          }),
          confirmLabel: enabling ? t('Offices.consultorio.enable') : t('Offices.consultorio.disable'),
          danger: !enabling,
        };
      }
      case 'deleteDisplay':
        return {
          title: t('Offices.confirm.deleteDisplayTitle'),
          message: t('Offices.confirm.deleteDisplayMessage', { name: action.display.name || action.display.registrationNumber }),
          confirmLabel: t('Offices.actions.delete'),
          danger: true,
        };
      case 'testCall':
        return {
          title: t('Offices.confirm.testCallTitle'),
          message: t('Offices.confirm.testCallMessage', { name: action.display.name || action.display.registrationNumber }),
          confirmLabel: t('Offices.display.testCall'),
          danger: false,
        };
    }
  };

  const actionErrorTitle = (action: ConfirmAction) =>
    action.kind === 'testCall'
      ? t('Offices.messages.testCallError')
      : action.kind === 'toggleConsultorio'
      ? t('Offices.messages.statusError')
      : t('Offices.messages.deleteError');

  const anyLoading = sedesStatus.isLoading || sectoresStatus.isLoading || consultoriosStatus.isLoading || displaysStatus.isLoading;

  const kpi = (label: string, value: number, detail: string, tone?: 'brand' | 'success' | 'warning', testId?: string) => (
    <div className={styles.kpi} data-testid={testId}>
      <span className={styles.kpiLabel}>{label}</span>
      <span
        className={mergeClasses(
          styles.kpiValue,
          tone === 'brand' && styles.kpiBrand,
          tone === 'success' && styles.kpiSuccess,
          tone === 'warning' && styles.kpiWarning
        )}
      >
        {value}
      </span>
      <span className={styles.kpiDetail}>{detail}</span>
    </div>
  );

  return (
    <div className={styles.root}>
      <div className={styles.intro}>
        <div className={styles.introText}>
          <h2 className={styles.sectionTitle}>{t('Offices.workspaceTitle')}</h2>
          <span className={styles.subtitle}>{t('Offices.workspaceSubtitle')}</span>
        </div>
        <Button appearance="secondary" icon={<ArrowClockwise20Regular />} onClick={loadAll} disabled={anyLoading}>
          {t('Offices.actions.refresh')}
        </Button>
      </div>

      <div className={styles.kpis} aria-label={t('Offices.summary.title')} role="group">
        {kpi(t('Offices.summary.sedes'), summary.sedes, t('Offices.summary.sectores', { count: summary.sectores }), 'brand', 'kpi-sedes')}
        {kpi(
          t('Offices.summary.consultorios'),
          summary.consultorios,
          t('Offices.summary.consultoriosDetail', { disponibles: summary.consultoriosDisponibles, ocupados: summary.consultoriosOcupados }),
          'success',
          'kpi-consultorios'
        )}
        {kpi(
          t('Offices.summary.displays'),
          summary.displays,
          t('Offices.summary.displaysDetail', { activos: summary.displaysActivos }),
          'brand',
          'kpi-displays'
        )}
        {kpi(
          t('Offices.summary.unpaired'),
          summary.displaysSinVincular,
          t('Offices.summary.unpairedDetail'),
          summary.displaysSinVincular > 0 ? 'warning' : undefined,
          'kpi-unpaired'
        )}
      </div>

      <div className={styles.banners}>
        {loadError && (
          <ErrorBanner title={t('Offices.messages.loadError')} error={loadError.error} onRetry={retryFailedLoads} testId="offices-load-error" />
        )}
        {actionError && (
          <ErrorBanner
            title={actionErrorTitle(actionError.action)}
            error={actionError.error}
            onRetry={() => runAction(actionError.action)}
            testId="offices-action-error"
          />
        )}
      </div>

      <TabList
        className={styles.tabs}
        selectedValue={tab}
        onTabSelect={(_, d) => setTab(d.value as OfficesTab)}
        aria-label={t('Offices.title')}
      >
        <Tab value="structure" data-testid="tab-structure">
          {t('Offices.tabs.structure')}
          <span className={styles.tabCount}>{summary.sedes}</span>
        </Tab>
        <Tab value="consultorios" data-testid="tab-consultorios">
          {t('Offices.tabs.consultorios')}
          <span className={styles.tabCount}>{summary.consultorios}</span>
        </Tab>
        <Tab value="displays" data-testid="tab-displays">
          {t('Offices.tabs.displays')}
          <span className={styles.tabCount}>{summary.displays}</span>
        </Tab>
      </TabList>

      {tab === 'structure' && (
        <SedesTab
          sedes={sedes}
          sectores={sectores}
          consultorios={consultorios}
          displays={displays}
          isLoading={sedesStatus.isLoading || sectoresStatus.isLoading}
          selectedSectorId={selectedSectorId}
          onCreateSede={() => openSede()}
          onEditSede={(sede) => openSede(sede)}
          onDeleteSede={(sede) => setConfirm({ kind: 'deleteSede', sede })}
          onCreateSector={(sedeId) => openSector(undefined, sedeId)}
          onEditSector={(sector) => openSector(sector)}
          onDeleteSector={(sector) => setConfirm({ kind: 'deleteSector', sector })}
          onShowRooms={(sector) => showSectorIn('consultorios', sector)}
          onShowDisplays={(sector) => showSectorIn('displays', sector)}
        />
      )}
      {tab === 'consultorios' && (
        <ConsultoriosTab
          consultorios={consultorios}
          sectores={sectores}
          sedes={sedes}
          isLoading={consultoriosStatus.isLoading}
          filter={consultoriosFilter}
          selectedId={selectedConsultorioId}
          onFilterChange={setConsultoriosFilter}
          onCreate={() => openConsultorio()}
          onEdit={(c) => openConsultorio(c)}
          onToggleStatus={(c) =>
            setConfirm({
              kind: 'toggleConsultorio',
              consultorio: c,
              next: normalizeConsultorioStatus(c.status) === 'noDisponible' ? CONSULTORIO_STATUS.disponible : CONSULTORIO_STATUS.noDisponible,
            })
          }
          onDelete={(c) => setConfirm({ kind: 'deleteConsultorio', consultorio: c })}
        />
      )}
      {tab === 'displays' && (
        <DisplaysTab
          displays={displays}
          sectores={sectores}
          sedes={sedes}
          consultorios={consultorios}
          isLoading={displaysStatus.isLoading}
          filter={displaysFilter}
          selectedId={selectedDisplayId}
          onFilterChange={setDisplaysFilter}
          onCreate={() => openDisplay()}
          onPair={() => setPairingOpen(true)}
          onEdit={(d) => openDisplay(d)}
          onDelete={(d) => setConfirm({ kind: 'deleteDisplay', display: d })}
          onTestCall={(d, medicoId) => setConfirm({ kind: 'testCall', display: d, medicoId })}
        />
      )}

      <SedeDrawer
        open={sedeDrawer.open}
        sede={sedeDrawer.sede}
        sedes={sedes}
        isSaving={sedesStatus.isAdding || sedesStatus.isUpdating}
        saveError={sedeSaveError}
        onSubmit={submitSede}
        onClose={() => setSedeDrawer({ open: false })}
      />
      <DisplayDrawer
        open={displayDrawer.open}
        display={editingDisplay}
        defaults={{ sedeId: displaysFilter.sedeId, sectorId: displaysFilter.sectorId }}
        sedes={sedes}
        sectores={sectores}
        consultorios={consultorios}
        displays={displays}
        isSaving={displaysStatus.isAdding || displaysStatus.isUpdating}
        saveError={displaySaveError}
        onSubmit={submitDisplay}
        onEditSectorRooms={(sectorId) => openSector(findById(sectores, sectorId))}
        onClose={() => setDisplayDrawer({ open: false })}
      />
      <SectorDrawer
        open={sectorDrawer.open}
        sector={sectorDrawer.sector}
        defaultSedeId={sectorDrawer.defaultSedeId}
        sedes={sedes}
        sectores={sectores}
        consultorios={consultorios}
        isSaving={sectoresStatus.isAdding || sectoresStatus.isUpdating}
        saveError={sectorSaveError}
        onCreate={(payload) => {
          startSectorSave('create');
          props.CreateSector(payload);
        }}
        onUpdate={(id, payload) => {
          startSectorSave('update');
          props.UpdateSector(id, payload);
        }}
        onClose={() => setSectorDrawer({ open: false })}
      />
      <ConsultorioDrawer
        open={consultorioDrawer.open}
        consultorio={consultorioDrawer.consultorio}
        defaultSectorId={consultoriosFilter.sectorId}
        sedes={sedes}
        sectores={sectores}
        consultorios={consultorios}
        isSaving={consultoriosStatus.isAdding || consultoriosStatus.isUpdating}
        saveError={consultorioSaveError}
        onSubmit={submitConsultorio}
        onClose={() => setConsultorioDrawer({ open: false })}
      />
      <DisplayPairingDialog
        open={pairingOpen}
        displays={displays}
        defaults={{ sedeId: displaysFilter.sedeId, sectorId: displaysFilter.sectorId }}
        isCreating={displaysStatus.isAdding}
        createFailed={displaysStatus.addFailed}
        error={displaysStatus.error}
        onCreate={(payload) => props.CreateQueueDisplayScreen(payload)}
        onPoll={(id) => props.GetQueueDisplayScreenById(id)}
        onPaired={(display) => {
          setPairingOpen(false);
          setSelectedDisplayId(display.id);
          notify(t('Offices.messages.displayPaired', { name: display.name || display.registrationNumber }));
          props.GetQueueDisplayScreens();
        }}
        onClose={() => {
          setPairingOpen(false);
          props.GetQueueDisplayScreens();
        }}
      />
      {confirm && (
        <OfficesConfirmDialog
          open
          {...confirmTexts(confirm)}
          isProcessing={Boolean(running) && runningFlags.busy}
          onConfirm={() => runAction(confirm)}
          onCancel={() => setConfirm(null)}
        />
      )}
      <Toaster toasterId={toasterId} position="bottom-start" />
    </div>
  );
};

export default connector(OfficesWorkspace);
